import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useForm } from "react-hook-form";
import { AccountSettingForm } from "~/components/specifics/vendors/settings/accounts/AccountSettingForm";
import type { VendorCompanyUpdatePasswordParam } from "~/models/VendorCompany";
import {
	commonNewPasswordValidationSchema,
	requireOldPasswordValidationSchema
} from "~/models/VendorCompany";
import type { VendorSession } from "~/models/VendorSession";
import { isSuccess } from "~/utils/api/api_handler";
import { changePassword } from "~/utils/api/vendors/VendorCompany";
import { PASSWORD_RULE_HELP_TEXT } from "~/utils/PasswordValidationUtil";

type Props = {
	session: VendorSession;
};

export function AccountSetting({ session }: Props) {
	const { notify } = useNotification();
	const isAdmin = session.admin;

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, isSubmitting },
		reset
	} = useForm<VendorCompanyUpdatePasswordParam>({
		defaultValues: {
			confirmPassword: "",
			newPassword: "",
			oldPassword: ""
		},
		mode: "onBlur",
		resolver: yupResolver(
			isAdmin
				? commonNewPasswordValidationSchema
				: requireOldPasswordValidationSchema
		)
	});

	const onSubmit = async (
		passwords: VendorCompanyUpdatePasswordParam
	): Promise<void> => {
		const result = await changePassword(passwords);
		if (isSuccess(result)) {
			reset();
			notify({ content: "パスワードを変更しました。", variant: "success" });
		} else {
			notify({
				content: result.message ?? "エラーが発生しました。",
				variant: "error"
			});
		}
	};
	return (
		<Panel>
			<PanelHeader title="アカウント詳細" />
			<PanelBody>
				{!session.vendorCompany.enabled && (
					<Alertv2 mb={designTokens["spacing/m"]} variant="warning">
						<Paragraph>
							{
								"この施工会社は「取引終了」です。案件の依頼はできませんが、代理ログインによる操作と過去案件の閲覧は可能です。"
							}
						</Paragraph>
					</Alertv2>
				)}
				<AccountSettingForm session={session} />
				<Heading as="h3" mb={designTokens["spacing/m"]}>
					パスワードの変更
				</Heading>
				<form noValidate onSubmit={handleSubmit(onSubmit)}>
					<FormGroup maxW="32rem" variant="vertical">
						<FormControl
							chip={isAdmin ? undefined : "必須"}
							isInvalid={!!errors.oldPassword}
							label="現在のパスワード"
						>
							<Input
								{...register("oldPassword")}
								autoComplete="current-password"
								disabled={isAdmin}
								type="password"
							/>
							<FormControlText error={errors.oldPassword?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.newPassword}
							label="新しいパスワード"
						>
							<Input
								{...register("newPassword")}
								autoComplete="new-password"
								type="password"
							/>
							<FormControlText
								error={errors.newPassword?.message}
								help={PASSWORD_RULE_HELP_TEXT}
							/>
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.confirmPassword}
							label="新しいパスワード（確認用）"
						>
							<Input
								{...register("confirmPassword")}
								autoComplete="new-password"
								type="password"
							/>
							<FormControlText error={errors.confirmPassword?.message} />
						</FormControl>
					</FormGroup>
					<Button
						disabled={!isDirty}
						loading={isSubmitting}
						mt={designTokens["spacing/m"]}
						type="submit"
						variant="primary"
					>
						パスワードを変更
					</Button>
				</form>
			</PanelBody>
		</Panel>
	);
}
